import { type LicensedEstablishment } from 'types/api'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import { useApiClient } from 'hooks/useApiClient'
import { type FileImageRequest } from 'types/fileUpload'

interface UsePutImageLicensedEstablishmentProps {
  id: number
  onSuccess?: (data: LicensedEstablishment, variables: FileImageRequest) => void
  onError?: (error: Error, variables: FileImageRequest) => void
}

export const usePutImageLicensedEstablishment = ({
  id,
  onSuccess,
  onError,
}: UsePutImageLicensedEstablishmentProps) => {
  const putAPIClient = useApiClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (licensedEstablishment: FileImageRequest) => {
      const body = new FormData()
      body.set('FileUpload', licensedEstablishment.ImageFile)
      body.set('FileUploadName', licensedEstablishment.ImageFileName)
      body.set('FileUploadType', licensedEstablishment.ImageFileType)

      const { data, error } = await putAPIClient.put(
        '/admin/corporate-accounts/licensed-establishments/{id}/upload',
        {
          params: { path: { id } },
          body: {},
          bodySerializer: () => body,
        }
      )
      return { data, error }
    },
    onSuccess: async ({ data, error }, variables: FileImageRequest) => {
      await queryClient.invalidateQueries({
        queryKey: [
          '/admin/corporate-accounts/licensed-establishments/{id}',
          id,
        ],
      })
      if (error !== undefined) {
        onError?.(error as Error, variables)
      } else {
        onSuccess?.(data as LicensedEstablishment, variables)
      }
    },
    onError,
  })
}
